import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ElMessage } from 'element-plus';
import {
  createDocumentFromUpload,
  getSaveAsTemplateBasicInfo,
} from '@/api/documentWorkbenchApi';
import { formatApiErrorMessage } from '@/api/http';
import { useAiAssistantStore } from '@/stores/useAiAssistantStore';
import { useSmartDocumentAiStore } from '@/stores/useSmartDocumentAiStore';
import {
  isSmartDocumentScenario,
  resolveSmartDocumentScenario,
} from '@/utils/aiSessionContext';
import { thoughtStepsToJson, type ThoughtStep } from '@/utils/aiThought';

export type SmartDocumentSaveAsFromFileStructured = {
  kind: 'smart_document_save_as_from_file';
  status: 'pending' | 'wizard_opened' | 'done' | 'failed';
  fileName?: string;
  documentId?: string;
  templateName?: string;
  errorMessage?: string;
};

const IMPORTABLE_EXT = /\.(docx?|pptx?|xlsx?|txt|md|markdown|html?)$/i;

export function isImportableTemplateFile(file: File | undefined | null): boolean {
  if (!file?.name) return false;
  return IMPORTABLE_EXT.test(file.name);
}

/**
 * AI 附件导入 → 上传为工作台文档后打开「另存为模版」向导（人工审核），
 * 不再自动组装 reviewActions 一键保存。
 */
export const useSmartDocumentSaveAsFromFileStore = defineStore('smartDocumentSaveAsFromFile', () => {
  const active = ref(false);
  const processing = ref(false);
  const pendingMessageId = ref<string | null>(null);
  const pendingDocumentId = ref('');
  const lastOpenedDocumentId = ref('');

  const boundScenario = ref('');
  const boundSessionId = ref('');

  const aiStore = useAiAssistantStore();
  const smartDocAi = useSmartDocumentAiStore();

  function bindPanelContext(ctx: { scenario: string; sessionId: string }) {
    boundScenario.value = (ctx.scenario || '').trim();
    boundSessionId.value = (ctx.sessionId || '').trim();
  }

  function resolveScenario(): string {
    if (boundScenario.value && isSmartDocumentScenario(boundScenario.value)) {
      return boundScenario.value;
    }
    const scenario = resolveSmartDocumentScenario(smartDocAi.workspaceId || '0');
    if (!isSmartDocumentScenario(aiStore.currentScenario) || aiStore.currentScenario !== scenario) {
      aiStore.currentScenario = scenario;
    }
    return scenario;
  }

  function addMessage(
    role: 'user' | 'assistant',
    content: string,
    structured?: Record<string, unknown> | null,
    thought?: string,
  ) {
    const scenario = resolveScenario();
    const sessionId = boundSessionId.value || undefined;
    if (sessionId) {
      return aiStore.addMessage(scenario, sessionId, role, content, undefined, thought, structured);
    }
    return aiStore.addMessage(scenario, role, content, undefined, thought, structured);
  }

  function endFlow() {
    active.value = false;
    processing.value = false;
    pendingMessageId.value = null;
    pendingDocumentId.value = '';
  }

  function updateProgressMessage(
    steps: ThoughtStep[],
    structured: SmartDocumentSaveAsFromFileStructured,
    content?: string,
  ) {
    const id = pendingMessageId.value;
    if (!id) return;
    const scenario = resolveScenario();
    const sessionId = boundSessionId.value || undefined;
    aiStore.updateMessage(
      scenario,
      id,
      {
        ...(content != null ? { content } : {}),
        thought: thoughtStepsToJson(steps),
        structuredResult: structured as unknown as Record<string, unknown>,
      },
      sessionId,
    );
  }

  async function startFromFile(file: File, userText?: string): Promise<void> {
    if (!isImportableTemplateFile(file)) {
      ElMessage.warning('请上传 doc/docx/ppt/pptx/xls/xlsx/txt/md/html 文件');
      return;
    }
    if (processing.value) {
      ElMessage.warning('正在处理上一次导入，请稍候');
      return;
    }

    active.value = true;
    processing.value = true;
    pendingDocumentId.value = '';
    lastOpenedDocumentId.value = '';

    const workspaceId = smartDocAi.workspaceId || '0';
    const displayText = (userText || '').trim()
      || `请根据附件「${file.name}」创建文档模版`;

    addMessage('user', displayText);

    const steps: ThoughtStep[] = [];
    const pushStep = (key: string, title: string, text: string) => {
      steps.push({ key, icon: '📄', title, text });
    };

    try {
      pushStep('upload', '识别文件', `正在解析「${file.name}」…`);
      const progressMsg = addMessage(
        'assistant',
        '已收到文件，正在上传并打开模版提炼向导…',
        {
          kind: 'smart_document_save_as_from_file',
          status: 'pending',
          fileName: file.name,
        } satisfies SmartDocumentSaveAsFromFileStructured,
        thoughtStepsToJson(steps),
      );
      pendingMessageId.value = progressMsg.id;

      const created = await createDocumentFromUpload(workspaceId, file, { templateBackup: true });
      pendingDocumentId.value = created.documentId;
      lastOpenedDocumentId.value = created.documentId;
      steps[steps.length - 1].text = `已识别为文档「${created.title}」`;
      pushStep('basic', '基本信息', '锁定来源版本…');
      updateProgressMessage(steps, {
        status: 'pending',
        fileName: file.name,
        documentId: created.documentId,
        templateName: created.title,
      });

      let templateName = created.title;
      try {
        const basic = await getSaveAsTemplateBasicInfo(created.documentId);
        templateName = basic.templateName || created.title;
        steps[steps.length - 1].text = `模版名称：${templateName}`;
      } catch {
        steps[steps.length - 1].text = `模版名称：${templateName}`;
      }

      pushStep('wizard', '打开向导', '请在弹窗中完成结构识别、内容占位审阅与预览保存');
      smartDocAi.requestOpenSaveAsTemplate({
        documentId: created.documentId,
        dialogTitle: '创建模版',
      });

      updateProgressMessage(steps, {
        status: 'wizard_opened',
        fileName: file.name,
        documentId: created.documentId,
        templateName,
      }, '已打开模版提炼向导。请在弹窗中确认结构与占位后保存；完成后可在模版库查看。');

      ElMessage.success('已打开模版提炼向导，请完成人工审核');
      // 保持 active 至向导关闭由工作台刷新；此处结束 processing
      active.value = false;
    } catch (e) {
      const msg = formatApiErrorMessage(e) || '文件导入创建模版失败';
      pushStep('error', '失败', msg);
      updateProgressMessage(steps, {
        status: 'failed',
        fileName: file.name,
        errorMessage: msg,
      }, msg);
      ElMessage.error(msg);
      endFlow();
    } finally {
      processing.value = false;
    }
  }

  /** @deprecated 已改为向导内保存；保留空实现避免旧卡片报错 */
  async function confirmSave(_messageId: string): Promise<null> {
    ElMessage.info('请在模版提炼向导中完成结构确认与保存');
    return null;
  }

  return {
    active,
    processing,
    pendingMessageId,
    pendingDocumentId,
    lastOpenedDocumentId,
    bindPanelContext,
    startFromFile,
    confirmSave,
    endFlow,
  };
});
